import { useSearch } from '@elastic/react-search-ui'
import { InputViewProps } from '@elastic/react-search-ui-views'
import { SearchContextState } from '@elastic/search-ui'
import { autoUpdate, flip, shift, useFloating } from '@floating-ui/react-dom'
import { searchResultsData } from 'common/components/entities/search-results/utils'
import { useFloatHover } from 'common/hooks/useFloatHover'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTypedTranslation } from 'src/shared/i18next/hooks/use-typed-translation'
import { SearchIcon } from 'src/shared/icons/search-icon'
import {
  CommonSearchProps,
  SearchInputInterface,
} from 'src/shared/types/entities/search-interface'
import { SearchAutocompleteView } from '../search-autocomplete-view'
import {
  SearchAutocompleteDropdownUi,
  SearchAutocompleteWrapperUi,
} from '../search-autocomplete-view/ui'
import { SearchAutocompleteLoadingUi } from '../search-autocomplete-view/ui/search-autocomplete-loading-ui'
import { SearchAutocompleteLoadingWrapperUi } from '../search-autocomplete-view/ui/search-autocomplete-loading-wrapper-ui'
import { SearchAutocompleteNoResultsUi } from '../search-autocomplete-view/ui/search-autocomplete-no-results-ui'
import { SearchInputIconUi, SearchInputUi, SearchInputWrapperUi } from './ui'

interface SearchInputViewProps extends InputViewProps {
  input?: SearchInputInterface
  showDropdown?: boolean
  isPreviewMode?: boolean
  autocompleteProps: Omit<
    CommonSearchProps,
    'padding' | 'mobilePadding' | 'input'
  >
  onSubmit: (searchTerm: string) => void
  onInputClick?: () => void
}

interface ExtendedSearchContextStateInterface extends SearchContextState {
  autocompletedResultsRequestPending: boolean
  autocompletedSuggestionsRequestPending: boolean
  autocomplete: () => void
}

type SelectedType = {
  autocompletedResults: SearchContextState['autocompletedResults']
  autocompletedSuggestions: SearchContextState['autocompletedSuggestions']
  autocompletedResultsRequestPending: boolean
  autocompletedSuggestionsRequestPending: boolean
  setSearchTerm: SearchContextState['setSearchTerm']
  autocomplete: () => void
}

export const SearchInputView = ({
  getAutocomplete,
  getInputProps,
  input,
  showDropdown = false,
  isPreviewMode = false,
  autocompleteProps,
  onSubmit,
}: SearchInputViewProps) => {
  const { t } = useTypedTranslation()
  const { show, delayOpen, delayClose } = useFloatHover()
  const forcedOpen = !!showDropdown
  const isOpen = showDropdown || show
  const inputRef = useRef<HTMLInputElement | null>(null)
  const { refs, floatingStyles, update } = useFloating<HTMLDivElement>({
    placement: 'bottom',
    middleware: [flip(), shift()],
    whileElementsMounted: autoUpdate,
    open: isOpen ? true : undefined,
    strategy: 'absolute',
  })
  const [forceOpenLoading, setForceOpenLoading] = useState(false)
  const baseInputProps = getInputProps(
    forcedOpen
      ? { ref: inputRef, onFocus: undefined, onBlur: undefined }
      : {
          ref: inputRef,
          onFocus: () => delayOpen(),
          onBlur: () => delayClose(),
        },
  )

  const {
    autocompletedResults,
    autocompletedSuggestions,
    autocompletedResultsRequestPending,
    autocompletedSuggestionsRequestPending,
    setSearchTerm,
    autocomplete: runAC,
  } = useSearch<SelectedType>((s: SearchContextState) => {
    const ext = s as ExtendedSearchContextStateInterface

    return {
      autocompletedResults: ext.autocompletedResults,
      autocompletedSuggestions: ext.autocompletedSuggestions,
      autocompletedResultsRequestPending:
        ext.autocompletedResultsRequestPending,
      autocompletedSuggestionsRequestPending:
        ext.autocompletedSuggestionsRequestPending,
      setSearchTerm: ext.setSearchTerm,
      autocomplete: ext.autocomplete,
    }
  })

  const handleClickOpen = useCallback(() => {
    delayOpen()
    setForceOpenLoading(true)
    const val = inputRef.current?.value ?? ''

    if (val.length >= 3) {
      setForceOpenLoading(true)
      setSearchTerm?.(val, { refresh: false })
      runAC?.()
      if (!runAC && typeof baseInputProps.onChange === 'function') {
        baseInputProps.onChange({ target: { value: val + '\u200B' } })
        setSearchTerm?.(val, { refresh: false })
      }
    }
  }, [delayOpen, baseInputProps.onChange])

  const mergedInputProps = useMemo(
    () =>
      getInputProps({
        ...baseInputProps,
        onClick: (e: React.MouseEvent<HTMLInputElement>) => {
          baseInputProps.onClick?.(e)
          handleClickOpen()
        },
      }),
    [baseInputProps, handleClickOpen],
  )

  useEffect(() => {
    if (forcedOpen) {
      queueMicrotask(() => update())
    }
  }, [forcedOpen])

  useEffect(() => {
    if (isOpen) {
      queueMicrotask(() => update())
    }
  }, [isOpen, update])

  useEffect(() => {
    if (!forceOpenLoading) {
      return
    }
    const pending =
      autocompletedResultsRequestPending === true ||
      autocompletedSuggestionsRequestPending === true

    const suggestionsCount = Array.isArray(autocompletedSuggestions)
      ? autocompletedSuggestions.length
      : Object.values(autocompletedSuggestions ?? {}).reduce(
          (sum, group) => sum + group.length,
          0,
        )

    const hasData =
      (autocompletedResults?.length ?? 0) > 0 || suggestionsCount > 0
    if (pending || hasData) {
      setForceOpenLoading(false)
    }
  }, [
    forceOpenLoading,
    autocompletedResults,
    autocompletedSuggestions,
    autocompletedResultsRequestPending,
    autocompletedSuggestionsRequestPending,
  ])

  return (
    <SearchInputWrapperUi ref={refs.setReference}>
      <SearchInputUi {...mergedInputProps} {...input} />
      <SearchInputIconUi
        type="button"
        onClick={() => onSubmit(inputRef.current?.value || '')}
      >
        <SearchIcon width={20} height={20} />
      </SearchInputIconUi>
      <div
        ref={refs.setFloating}
        style={{
          ...floatingStyles,
          zIndex: 49,
          visibility: isOpen ? 'visible' : 'hidden',
          pointerEvents: isOpen ? 'auto' : 'none',
          width: '100%',
        }}
      >
        {isPreviewMode ? (
          <SearchAutocompleteView
            autocompleteResults={true}
            isPreviewMode={true}
            autocompletedResults={[
              {
                data: searchResultsData(3),
                total: 4,
              },
            ]}
            // biome-ignore lint/suspicious/noEmptyBlockStatements: <explanation>
            getItemProps={() => {}}
            // biome-ignore lint/suspicious/noEmptyBlockStatements: <explanation>
            getMenuProps={() => {}}
            {...autocompleteProps}
          />
        ) : (inputRef.current?.value?.length ?? 0) < 3 ? (
          <SearchAutocompleteWrapperUi>
            <SearchAutocompleteDropdownUi {...autocompleteProps.dropdown}>
              <SearchAutocompleteNoResultsUi>
                {t('components.search.enter_more_chars_hint')}
              </SearchAutocompleteNoResultsUi>
            </SearchAutocompleteDropdownUi>
          </SearchAutocompleteWrapperUi>
        ) : forceOpenLoading ? (
          <SearchAutocompleteWrapperUi>
            <SearchAutocompleteDropdownUi {...autocompleteProps.dropdown}>
              <SearchAutocompleteLoadingWrapperUi>
                <SearchAutocompleteLoadingUi />
              </SearchAutocompleteLoadingWrapperUi>
            </SearchAutocompleteDropdownUi>
          </SearchAutocompleteWrapperUi>
        ) : (
          getAutocomplete()
        )}
      </div>
    </SearchInputWrapperUi>
  )
}
